import { Box, Stack, Text, VStack } from '@chakra-ui/react';

import { Button } from '@m/components-ui/button';
import BottomSheet from '@m/components-ui/sheets/bottom-sheet';
import { type CiaLaneView } from '@m/types/api';
import {
  type CiaNearbyPromptVariant,
  type CiaSiteLaneGroup,
} from 'apps/customer/utils/ciaNearbyGatePrompt';

const SELECT_GATE_SUBTITLE = 'Select a gate below to open as you approach.';

type CiaNearbyGateSheetProps = {
  groups: CiaSiteLaneGroup[];
  onDismiss: () => void;
  onSelectLane: (lane: CiaLaneView) => void;
  open: boolean;
  variant: CiaNearbyPromptVariant | null;
};

function LaneButtons({
  lanes,
  layout,
  onSelectLane,
  primary,
}: {
  lanes: CiaLaneView[];
  layout: 'stack' | 'row';
  onSelectLane: (lane: CiaLaneView) => void;
  primary?: boolean;
}) {
  if (layout === 'row') {
    return (
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} width="100%">
        {lanes.map((lane) => (
          <Button
            key={lane.ciaGateAccessUuid}
            variant="secondary"
            width="100%"
            onClick={() => onSelectLane(lane)}
            data-testid={`cia-nearby-lane-${lane.laneId}`}
          >
            {lane.laneName}
          </Button>
        ))}
      </Box>
    );
  }

  return (
    <Stack gap={2} width="100%">
      {lanes.map((lane) => (
        <Button
          key={lane.ciaGateAccessUuid}
          variant={primary ? 'primary' : 'secondary'}
          width="100%"
          onClick={() => onSelectLane(lane)}
          data-testid={primary ? 'cia-nearby-open-gate-button' : `cia-nearby-lane-${lane.laneId}`}
        >
          {lane.laneName}
        </Button>
      ))}
    </Stack>
  );
}

export function CiaNearbyGateSheet({
  groups,
  onDismiss,
  onSelectLane,
  open,
  variant,
}: CiaNearbyGateSheetProps) {
  if (variant == null) return null;

  const isMultiZone = variant === 'multiZone';
  const title = isMultiZone ? 'Multiple gates nearby' : 'Arriving at your destination?';

  return (
    <BottomSheet
      open={open}
      onDismiss={onDismiss}
      showCloseButton
      showHandle
      size={isMultiZone ? 'full' : 'default'}
      title={title}
      subtitle={SELECT_GATE_SUBTITLE}
    >
      <Box data-testid="cia-nearby-gate-sheet">
        <VStack gap={6} align="stretch" pt={2} pb={isMultiZone ? 4 : 2}>
          {groups.map((group, index) => (
            <Box key={group.siteId}>
              {isMultiZone && index > 0 ? (
                <Box borderTopWidth="1px" borderColor="border.subtle" mb={6} />
              ) : null}
              <Text variant="MainBodyDemiBold" color="text.primary" mb={4}>
                {group.siteName}
              </Text>
              <LaneButtons
                lanes={group.lanes}
                layout={isMultiZone && group.lanes.length > 1 ? 'row' : 'stack'}
                onSelectLane={onSelectLane}
                primary={variant === 'single'}
              />
            </Box>
          ))}
        </VStack>
      </Box>
    </BottomSheet>
  );
}
